import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const LanguageContext = createContext(null);
const STORAGE_KEY = "arogya.lang";

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem(STORAGE_KEY) || "en";
  });
  const [languages, setLanguages] = useState([]);
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);

  // Fetch language catalog once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/i18n/languages");
        setLanguages(data.languages || []);
      } catch (err) {
        console.warn("[i18n] Failed to load language catalog:", err?.message || err);
      }
    })();
  }, []);

  // Fetch messages when lang changes
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/i18n/messages/${lang}`);
        setMessages(data.messages || {});
      } catch (err) {
        console.warn("[i18n] Failed to load messages for '" + lang + "':", err?.message || err);
        setMessages({});
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!next) return;
    localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
    // Note: HTML lang attribute for accessibility
    if (typeof document !== "undefined") {
      document.documentElement.lang = next;
    }
  }, []);

  const t = useCallback((key, fallback) => {
    return messages[key] || fallback || key;
  }, [messages]);

  /** On-demand translation for dynamic content (bios, blurbs). */
  const translate = useCallback(async (text) => {
    if (!text || lang === "en") return text;
    setTranslating(true);
    try {
      const { data } = await api.post("/i18n/translate", { target_lang: lang, text });
      return data.translated;
    } catch {
      return text;
    } finally {
      setTranslating(false);
    }
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, languages, messages, loading, translating, t, translate }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
